// Finds web addresses in free text (bios, education, admin notes). People write them
// every way: "https://x.com/a", "www.x.com", bare "soygalo.com", "linkedin.com/in/me",
// inside parentheses or glued to a name ("Vora(https://vora.app),"), with trailing
// punctuation. Emails are left alone.

export type TextPart =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; href: string };

// Bare domains (no scheme, no www) are only recognised under these TLDs, so "Node.js",
// "e.g." or a sentence missing its space ("fin.Luego") are not mistaken for sites.
const TLDS = [
  'com',
  'dev',
  'io',
  'me',
  'app',
  'ai',
  'cl',
  'co',
  'net',
  'org',
  'xyz',
  'tech',
  'site',
  'online',
  'fun',
  'build',
  'cloud',
  'page',
  'so',
  'gg',
  've',
  'mx',
  'ar',
  'pe',
  'uy',
  'py',
  'br',
  'bo',
  'ec',
  'es',
  'us',
  'edu',
  'gob',
  'gov',
].join('|');

const LABEL = '[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]*[\\p{L}\\p{N}])?';
const PATH = '(?:[/?#][^\\s<>"]*)?';

const URL_PATTERN = new RegExp(
  [
    // Explicit scheme, or www.
    `(?:https?:\\/\\/|www\\.)[^\\s<>"]+`,
    // Bare domain: not glued to a word, an email local part or another path.
    `(?<![\\p{L}\\p{N}_@./-])(?:${LABEL}\\.)+(?:${TLDS})(?![\\p{L}\\p{N}_@-])(?::\\d+)?${PATH}`,
  ].join('|'),
  'giu',
);

// Looks like a domain but is a technology name.
const NOT_SITES = new Set(['asp.net', 'vue.js', 'next.js', 'node.js']);

const TRAILING = /[.,;:!?'"»”’]+$/;
const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

// Drops sentence punctuation and any closing bracket the link did not open.
function trimTrailing(candidate: string): string {
  let url = candidate;
  for (;;) {
    const stripped = url.replace(TRAILING, '');
    const last = stripped.slice(-1);
    const opener = CLOSERS[last];
    if (opener) {
      const opens = stripped.split(opener).length - 1;
      const closes = stripped.split(last).length - 1;
      if (closes > opens) {
        url = stripped.slice(0, -1);
        continue;
      }
    }
    if (stripped === url) return url;
    url = stripped;
  }
}

function toHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function splitLinks(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let cursor = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const url = trimTrailing(match[0]);
    const start = match.index ?? 0;
    const host = url.replace(/^https?:\/\//i, '').split(/[/?#:]/)[0];
    // "www." alone, or a lone TLD-looking token, is not a link.
    if (!/[\p{L}\p{N}]\.[\p{L}]/u.test(host)) continue;
    if (NOT_SITES.has(host.toLowerCase())) continue;

    if (start > cursor) {
      parts.push({ type: 'text', text: text.slice(cursor, start) });
    }
    parts.push({ type: 'link', text: url, href: toHref(url) });
    cursor = start + url.length;
  }

  if (cursor < text.length) {
    parts.push({ type: 'text', text: text.slice(cursor) });
  }
  return parts;
}
